import { useState, useMemo } from "react";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  BarChart3,
  TrendingUp,
  Users,
  Unlock,
  Download,
  Loader2,
  Euro,
  ThumbsUp,
  ThumbsDown,
  Minus,
  Star,
} from "lucide-react";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";

const PERIOD_OPTIONS = [
  { value: "7", label: "7 derniers jours" },
  { value: "30", label: "30 derniers jours" },
  { value: "90", label: "90 derniers jours" },
  { value: "365", label: "12 derniers mois" },
];

const GRANULARITY_OPTIONS = [
  { value: "day", label: "Jour" },
  { value: "week", label: "Semaine" },
  { value: "month", label: "Mois" },
];

const PIE_COLORS = [
  "#3b82f6", "#ef4444", "#22c55e", "#f59e0b", "#8b5cf6",
  "#06b6d4", "#ec4899", "#14b8a6", "#f97316", "#6366f1",
];

function formatDateLabel(d: string, gran: string): string {
  const date = new Date(d);
  if (gran === "month") return date.toLocaleDateString("fr-FR", { month: "short", year: "2-digit" });
  return date.toLocaleDateString("fr-FR", { day: "2-digit", month: "short" });
}

function getDateRange(days: number): { dateFrom: string; dateTo: string } {
  const to = new Date();
  const from = new Date(to.getTime() - days * 86400000);
  return {
    dateFrom: from.toISOString().slice(0, 10),
    dateTo: to.toISOString().slice(0, 10),
  };
}

export default function MarketplaceStats() {
  const [periodDays, setPeriodDays] = useState("30");
  const [granularity, setGranularity] = useState<"day" | "week" | "month">("day");

  const { dateFrom, dateTo } = useMemo(
    () => getDateRange(parseInt(periodDays)),
    [periodDays]
  );

  const creditsQuery = trpc.admin.analytics.timeSeries.useQuery({
    metric: "creditsSold",
    granularity,
    dateFrom,
    dateTo,
  });

  const unlocksQuery = trpc.admin.analytics.timeSeries.useQuery({
    metric: "unlocks",
    granularity,
    dateFrom,
    dateTo,
  });

  const newProsQuery = trpc.admin.analytics.timeSeries.useQuery({
    metric: "newPros",
    granularity,
    dateFrom,
    dateTo,
  });

  const newProjectsQuery = trpc.admin.analytics.timeSeries.useQuery({
    metric: "newProjects",
    granularity,
    dateFrom,
    dateTo,
  });

  const categoryQuery = trpc.admin.analytics.timeSeries.useQuery({
    metric: "newProjects",
    granularity: "month",
    dateFrom,
    dateTo,
    groupBy: "category",
  });

  const npsQuery = trpc.admin.analytics.getNpsStats.useQuery(
    { days: parseInt(periodDays) },
  );

  const exportMutation = trpc.admin.analytics.exportTimeSeries.useMutation({
    onSuccess: (data) => {
      const blob = new Blob([data.csv], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = data.filename;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Export ${data.count} lignes téléchargé`);
    },
    onError: (err) => toast.error(err.message),
  });

  const totalCredits = (creditsQuery.data ?? []).reduce(
    (sum: number, d: any) => sum + (d.value ?? 0),
    0
  );
  const totalUnlocks = (unlocksQuery.data ?? []).reduce(
    (sum: number, d: any) => sum + (d.value ?? 0),
    0
  );
  const totalNewPros = (newProsQuery.data ?? []).reduce(
    (sum: number, d: any) => sum + (d.value ?? 0),
    0
  );
  const totalNewProjects = (newProjectsQuery.data ?? []).reduce(
    (sum: number, d: any) => sum + (d.value ?? 0),
    0
  );
  const conversionRate =
    totalNewProjects > 0
      ? ((totalUnlocks / totalNewProjects) * 100).toFixed(1)
      : "0";

  const categoryPieData = useMemo(() => {
    const groups = new Map<string, number>();
    for (const d of categoryQuery.data ?? []) {
      const g = (d as any).group || "Autre";
      groups.set(g, (groups.get(g) || 0) + ((d as any).value ?? 0));
    }
    return Array.from(groups.entries())
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 10);
  }, [categoryQuery.data]);

  const isLoading =
    creditsQuery.isLoading || unlocksQuery.isLoading || newProsQuery.isLoading || newProjectsQuery.isLoading;

  function handleExport(metric: "creditsSold" | "unlocks" | "newPros" | "newProjects") {
    exportMutation.mutate({ metric, granularity, dateFrom, dateTo });
  }

  if (isLoading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center py-20 text-muted-foreground text-sm gap-2">
          <Loader2 className="h-4 w-4 animate-spin" />
          Chargement des statistiques…
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <BarChart3 className="h-6 w-6" />
              Statistiques Marketplace
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Métriques clés TravauxBTP — crédits, déblocages, pros, projets
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Select value={periodDays} onValueChange={setPeriodDays}>
              <SelectTrigger className="h-8 w-40 text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PERIOD_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={granularity} onValueChange={(v) => setGranularity(v as any)}>
              <SelectTrigger className="h-8 w-32 text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {GRANULARITY_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              size="sm"
              className="h-8"
              disabled={exportMutation.isPending}
              onClick={() => handleExport("creditsSold")}
            >
              <Download className="h-3.5 w-3.5 mr-1" />
              CSV
            </Button>
          </div>
        </div>

        {/* KPI cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="py-4">
              <div className="flex items-center gap-2 mb-1">
                <Euro className="h-4 w-4 text-green-600" />
                <span className="text-xs text-muted-foreground">CA crédits</span>
              </div>
              <div className="text-2xl font-bold text-green-700">
                {totalCredits.toLocaleString("fr-FR")} €
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="py-4">
              <div className="flex items-center gap-2 mb-1">
                <Users className="h-4 w-4 text-blue-600" />
                <span className="text-xs text-muted-foreground">Nouveaux pros</span>
              </div>
              <div className="text-2xl font-bold text-blue-700">{totalNewPros}</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="py-4">
              <div className="flex items-center gap-2 mb-1">
                <Unlock className="h-4 w-4 text-purple-600" />
                <span className="text-xs text-muted-foreground">Déblocages</span>
              </div>
              <div className="text-2xl font-bold text-purple-700">{totalUnlocks}</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="py-4">
              <div className="flex items-center gap-2 mb-1">
                <TrendingUp className="h-4 w-4 text-orange-600" />
                <span className="text-xs text-muted-foreground">Taux conversion</span>
              </div>
              <div className="text-2xl font-bold text-orange-700">{conversionRate}%</div>
              <div className="text-xs text-muted-foreground">déblocages / projets</div>
            </CardContent>
          </Card>
        </div>

        {/* NPS Plateforme */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Star className="h-4 w-4 text-yellow-500" />
              NPS Plateforme — {periodDays} derniers jours
            </CardTitle>
          </CardHeader>
          <CardContent>
            {npsQuery.isLoading ? (
              <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Chargement NPS…
              </div>
            ) : npsQuery.data ? (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center">
                  <div
                    className={`text-3xl font-bold ${
                      npsQuery.data.npsScore >= 50
                        ? "text-green-600"
                        : npsQuery.data.npsScore >= 0
                          ? "text-yellow-600"
                          : "text-red-600"
                    }`}
                  >
                    {npsQuery.data.npsScore > 0 ? "+" : ""}
                    {npsQuery.data.npsScore}
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">Score NPS</div>
                </div>
                <div className="space-y-1.5">
                  <div className="flex items-center gap-1.5 text-sm">
                    <ThumbsUp className="h-3.5 w-3.5 text-green-600" />
                    <span className="text-green-700 font-medium">{npsQuery.data.promoters}</span>
                    <span className="text-muted-foreground text-xs">promoteurs</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-sm">
                    <Minus className="h-3.5 w-3.5 text-yellow-600" />
                    <span className="text-yellow-700 font-medium">{npsQuery.data.passives}</span>
                    <span className="text-muted-foreground text-xs">passifs</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-sm">
                    <ThumbsDown className="h-3.5 w-3.5 text-red-600" />
                    <span className="text-red-700 font-medium">{npsQuery.data.detractors}</span>
                    <span className="text-muted-foreground text-xs">détracteurs</span>
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold">{npsQuery.data.totalResponses}</div>
                  <div className="text-xs text-muted-foreground mt-1">réponses</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold">{npsQuery.data.responseRate}%</div>
                  <div className="text-xs text-muted-foreground mt-1">taux réponse</div>
                </div>
              </div>
            ) : (
              <div className="py-6 text-sm text-muted-foreground text-center">
                Aucune donnée NPS disponible
              </div>
            )}
          </CardContent>
        </Card>

        {/* Charts row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Credits line chart */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">CA crédits vendus</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={creditsQuery.data ?? []}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis
                    dataKey="period"
                    tick={{ fontSize: 11 }}
                    tickFormatter={(v) => formatDateLabel(v, granularity)}
                  />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip
                    labelFormatter={(v) => formatDateLabel(v as string, granularity)}
                    formatter={(v: number) => [`${v.toLocaleString("fr-FR")} €`, "CA"]}
                  />
                  <Line type="monotone" dataKey="value" stroke="#22c55e" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Unlocks bar chart */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Déblocages de leads</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={unlocksQuery.data ?? []}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis
                    dataKey="period"
                    tick={{ fontSize: 11 }}
                    tickFormatter={(v) => formatDateLabel(v, granularity)}
                  />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip
                    labelFormatter={(v) => formatDateLabel(v as string, granularity)}
                    formatter={(v: number) => [v, "Déblocages"]}
                  />
                  <Bar dataKey="value" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>

        {/* Category pie chart */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Projets par catégorie</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-center">
              {categoryPieData.length === 0 ? (
                <div className="py-12 text-sm text-muted-foreground">Aucune donnée</div>
              ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={categoryPieData}
                      cx="50%"
                      cy="50%"
                      outerRadius={100}
                      dataKey="value"
                      nameKey="name"
                      label={({ name, percent }) =>
                        `${name} (${(percent * 100).toFixed(0)}%)`
                      }
                    >
                      {categoryPieData.map((_, i) => (
                        <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(v: number) => [v, "Projets"]} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
